import React from "react";
import {
  Wallet, PlayCircle, FileText, Receipt, Layers, SlidersHorizontal, Check, Users, Lock,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { StatCard } from "../../components/StatCard";
import { Input, Field, Select } from "../../components/forms";
import { Grid, Stack, Row, Hint, StatusBadge } from "./kit";
import { useHrms, useFetch, fmtMoney, fullName, monthKey, CYCLE_MONTH_BOUNDS } from "./useHrms";

// HRMS ▸ Payroll — live pipeline: structure → run → compute → lock & publish.
// Statutory filings (PF/ESI/TDS returns) intentionally out of scope for v1.

const TABS = [
  { key: "run", label: "Current run", icon: <PlayCircle size={14} /> },
  { key: "runs", label: "Run history", icon: <Lock size={14} /> },
  { key: "payslips", label: "Payslips", icon: <FileText size={14} /> },
  { key: "structures", label: "Structures", icon: <Layers size={14} /> },
  { key: "components", label: "Components", icon: <SlidersHorizontal size={14} /> },
  { key: "reimb", label: "Reimbursements", icon: <Receipt size={14} /> },
];

export default function HrmsPayroll() {
  const t = useDashTheme();
  const [tab, setTab] = React.useState("run");

  return (
    <PageShell
      eyebrow="HRMS"
      title="Payroll"
      subtitle="Structure → compute → lock. A locked run never changes."
      icon={<Wallet size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "run" && <CurrentRun t={t} />}
      {tab === "runs" && <Runs t={t} />}
      {tab === "payslips" && <Payslips t={t} />}
      {tab === "structures" && <Structures t={t} />}
      {tab === "components" && <Components t={t} />}
      {tab === "reimb" && <Reimb t={t} />}
    </PageShell>
  );
}

// ── Current run ──
function CurrentRun({ t }) {
  const { isHr, post } = useHrms();
  const [month, setMonth] = React.useState(monthKey());
  const runs = useFetch(isHr ? "/payroll/runs" : null);
  const [busy, setBusy] = React.useState(false);
  const run = (runs.data || []).find((r) => r.cycleMonth === month) || null;
  // Who compute left out and why — silently paying fewer people than the org
  // employs is the kind of thing you want on screen before you lock. Read off
  // the run itself, so it is still there after a reload.
  const skipped = run?.skipped || [];
  const detail = useFetchRun(run?._id);

  if (!isHr) return <EmptyState title="HR only" description="Payroll runs are managed by HR. Your payslips are in My HR ▸ Payslips." />;

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); runs.refresh(); detail.refresh(); } finally { setBusy(false); }
  };
  const start = () => act(() => post("/payroll/runs", { cycleMonth: month }));
  const compute = () => act(() => post(`/payroll/runs/${run._id}/compute`, {}));
  const finalize = () => act(() => post(`/payroll/runs/${run._id}/finalize`, {}));

  const locked = run && ["locked", "paid"].includes(run.status);
  const computed = (detail.data?.payslips || []).length > 0;

  return (
    <Stack gap={t.s[4]}>
      <Row gap={12} align="flex-end" wrap>
        <Field label="Cycle month">
          <Input
            type="month" value={month} min={CYCLE_MONTH_BOUNDS.min} max={CYCLE_MONTH_BOUNDS.max}
            onChange={(e) => setMonth(e.target.value)}
          />
        </Field>
        {!run && <Button variant="primary" onClick={start} disabled={busy}>Start run</Button>}
        {run && !locked && <Button variant="secondary" onClick={compute} disabled={busy}>{computed ? "Recompute" : "Compute"}</Button>}
        {run && computed && !locked && <Button variant="primary" leftIcon={<Lock size={13} />} onClick={finalize} disabled={busy}>Lock & publish</Button>}
        {locked && <Badge tone="positive">Locked {run.lockDate ? new Date(run.lockDate).toLocaleDateString("en-GB") : ""}</Badge>}
      </Row>

      {run && (
        <>
          <Grid min={200}>
            <StatCard
              label="Employees paid"
              value={run.memberCount ? `${run.employeeCount || 0} of ${run.memberCount}` : (run.employeeCount || 0)}
              icon={<Users size={12} />}
              accent={skipped.length > 0 ? t.status.warning : undefined}
            />
            <StatCard label="Gross" value={fmtMoney(run.grossTotal)} icon={<Wallet size={12} />} />
            <StatCard label="Net" value={fmtMoney(run.netTotal)} icon={<Wallet size={12} />} />
            <StatCard label="Status" value={cap(run.status)} icon={<PlayCircle size={12} />} accent={locked ? t.status.positive : t.status.warning} />
          </Grid>

          <SectionCard title="Pipeline" padding={0}>
            <div>
              {(run.steps || []).map((s, i) => (
                <Row key={s.step} justify="space-between" style={{
                  padding: `${t.s[3]}px ${t.s[4]}px`,
                  borderBottom: i === run.steps.length - 1 ? "none" : `1px solid ${t.c.border}`,
                }}>
                  <Row gap={12}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 99, flexShrink: 0, display: "inline-flex",
                      alignItems: "center", justifyContent: "center", fontSize: t.font.size.xs,
                      background: s.state === "done" ? t.status.positiveSoft : t.c.surfaceMuted,
                      color: s.state === "done" ? t.status.positive : t.c.textTertiary,
                      border: `1px solid ${s.state === "done" ? t.status.positive : t.c.border}`,
                    }}>{s.state === "done" ? <Check size={12} /> : i + 1}</span>
                    <div>
                      <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{s.step}</div>
                      <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2 }}>{s.detail}</div>
                    </div>
                  </Row>
                  <Badge tone={s.state === "done" ? "positive" : s.state === "ready" ? "info" : "neutral"}>{cap(s.state)}</Badge>
                </Row>
              ))}
            </div>
          </SectionCard>

          {skipped.length > 0 && (
            <SectionCard title={`Left out of this run · ${skipped.length}`} padding={0}>
              <DataTable
                columns={[
                  { key: "name", header: "Employee", render: (r) => r.name || "—" },
                  { key: "reason", header: "Why they were left out" },
                ]}
                rows={skipped}
              />
            </SectionCard>
          )}

          <SectionCard title="Payslips in this run" padding={0}>
            <PayslipTable t={t} rows={detail.data?.payslips || []} loading={detail.loading}
              empty="Compute the run to generate payslips. Employees without a salary structure are skipped." />
          </SectionCard>
        </>
      )}

      {!run && <Hint>No run for {month} yet. Employees need an active salary structure (Structures tab) before compute picks them up.</Hint>}
    </Stack>
  );
}

// Runs list is fetched by CurrentRun already; tiny helper for run detail.
function useFetchRun(id) {
  return useFetch(id ? `/payroll/runs/${id}` : null, [id]);
}

function PayslipTable({ t, rows, loading, empty }) {
  return (
    <DataTable
      loading={loading}
      columns={[
        { key: "who", header: "Employee", render: (r) => (
          <Row gap={10}>
            <Avatar name={fullName(r.employeeId)} src={r.employeeId?.avatar} size={24} />
            <span>{fullName(r.employeeId)}</span>
          </Row>
        ) },
        { key: "month", header: "Month" },
        { key: "gross", header: "Gross", align: "right", render: (r) => <Num t={t}>{fmtMoney(r.gross)}</Num> },
        { key: "deductions", header: "Deductions", align: "right", render: (r) => <Num t={t} muted>{fmtMoney(r.deductions)}</Num> },
        { key: "lopDays", header: "LOP days", align: "right", render: (r) => r.lopDays || 0 },
        { key: "net", header: "Net", align: "right", render: (r) => <Num t={t} bold>{fmtMoney(r.net)}</Num> },
        { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
      ]}
      rows={rows}
      emptyState={<EmptyState title="No payslips" description={empty} />}
    />
  );
}

const Num = ({ t, children, muted, bold }) => (
  <span style={{ fontVariantNumeric: "tabular-nums", color: muted ? t.c.textSecondary : t.c.text, fontWeight: bold ? t.font.weight.medium : undefined }}>{children}</span>
);

// ── Run history ──
function Runs({ t }) {
  const { isHr } = useHrms();
  const { data, loading } = useFetch(isHr ? "/payroll/runs" : null);
  if (!isHr) return <EmptyState title="HR only" description="Run history is visible to HR admins." />;
  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Payroll runs" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "cycleMonth", header: "Cycle" },
            { key: "payGroup", header: "Pay group", render: (r) => <Badge tone="neutral">{r.payGroup}</Badge> },
            // "1" alone reads as a healthy run in a 3-person org. Show the
            // headcount it was measured against, and why anyone was left out.
            { key: "employeeCount", header: "Paid", align: "right",
              render: (r) => (r.memberCount ? `${r.employeeCount || 0} of ${r.memberCount}` : (r.employeeCount || 0)) },
            { key: "skipped", header: "Left out", align: "right",
              render: (r) => ((r.skipped || []).length
                ? (
                  <span title={r.skipped.map((s) => `${s.name || "—"}: ${s.reason}`).join("\n")}>
                    <Badge tone="warning">{r.skipped.length}</Badge>
                  </span>
                )
                : "—") },
            { key: "grossTotal", header: "Gross", align: "right", render: (r) => <Num t={t}>{fmtMoney(r.grossTotal)}</Num> },
            { key: "netTotal", header: "Net", align: "right", render: (r) => <Num t={t} bold>{fmtMoney(r.netTotal)}</Num> },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No runs yet" description="Start the first run from the Current run tab." />}
        />
      </SectionCard>
      <Hint>A locked run is immutable — corrections post as arrears or recoveries in the next cycle.</Hint>
    </Stack>
  );
}

// ── Payslips ──
function Payslips({ t }) {
  const [month, setMonth] = React.useState("");
  const { data, loading } = useFetch(`/payroll/payslips${month ? `?month=${month}` : ""}`);
  return (
    <Stack gap={t.s[4]}>
      <Row gap={12}>
        <Field label="Month"><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
      </Row>
      <SectionCard title="Payslips" subtitle="Employees see only their own published slips" padding={0}>
        <PayslipTable t={t} rows={data || []} loading={loading} empty="Published payslips appear here after a run is locked." />
      </SectionCard>
    </Stack>
  );
}

// ── Structures ──
function Structures({ t }) {
  const { isHr, put } = useHrms();
  const employees = useFetch(isHr ? "/employees" : null);
  const [sel, setSel] = React.useState("");
  const structure = useFetch(sel ? `/payroll/structures/${sel}` : null, [sel]);
  const [ctc, setCtc] = React.useState("");
  const [saved, setSaved] = React.useState(false);

  if (!isHr) return <EmptyState title="HR only" description="Salary structures are managed by HR." />;

  const save = async () => {
    await put(`/payroll/structures/${sel}`, { ctcAnnual: Number(ctc) });
    setSaved(true); setCtc("");
    structure.refresh();
  };

  const s = structure.data;
  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Salary structure" subtitle="Set annual CTC; components split it using the org's pay component rules" padding={16}>
        <Row gap={12} wrap align="flex-end">
          <Field label="Employee">
            <Select value={sel} onChange={(e) => { setSel(e.target.value); setSaved(false); }}>
              <option value="">Select…</option>
              {(employees.data?.employees || []).map((u) => <option key={u.userId} value={u.userId}>{u.name}</option>)}
            </Select>
          </Field>
          <Field label="Annual CTC (₹)"><Input type="number" value={ctc} onChange={(e) => setCtc(e.target.value)} placeholder="1200000" /></Field>
          <Button variant="primary" onClick={save} disabled={!sel || !ctc}>Apply structure</Button>
        </Row>
        {saved && <div style={{ marginTop: 10 }}><Badge tone="positive">Structure saved and audited.</Badge></div>}
      </SectionCard>

      {sel && (
        <SectionCard
          title={s ? `Active structure · CTC ${fmtMoney(s.ctcAnnual)}/yr` : "No structure yet"}
          subtitle={s ? `Effective ${new Date(s.effectiveFrom).toLocaleDateString("en-GB")}` : "Apply one above"}
          padding={0}
        >
          <DataTable
            loading={structure.loading}
            columns={[
              { key: "name", header: "Component", render: (r) => r.payComponentId?.name },
              { key: "part", header: "Type", render: (r) => <Badge tone={r.payComponentId?.part === "earning" ? "positive" : "warning"}>{cap(r.payComponentId?.part)}</Badge> },
              { key: "monthlyAmount", header: "Monthly", align: "right", render: (r) => <Num t={t}>{fmtMoney(r.monthlyAmount)}</Num> },
            ]}
            rows={s?.components || []}
            emptyState={<EmptyState title="No components" description="Apply a CTC to build the structure." />}
          />
        </SectionCard>
      )}
    </Stack>
  );
}

// ── Components ──
function Components({ t }) {
  const { isHr, post, patch } = useHrms();
  const { data, loading, refresh } = useFetch("/payroll/components");
  const [form, setForm] = React.useState({ name: "", part: "earning", calcType: "percent_of_gross", calcValue: "" });

  const add = async () => {
    if (!form.name.trim()) return;
    await post("/payroll/components", { ...form, calcValue: Number(form.calcValue) || 0 });
    setForm({ name: "", part: "earning", calcType: "percent_of_gross", calcValue: "" });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Pay components" subtitle="The rules that split CTC into a structure" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "name", header: "Component" },
            { key: "part", header: "Type", render: (r) => <Badge tone={r.part === "earning" ? "positive" : r.part === "deduction" ? "warning" : "info"}>{cap(r.part)}</Badge> },
            { key: "calc", header: "Calculation", render: (r) => r.calcType === "balancing" ? "Balance of gross" : `${r.calcValue}${r.calcType.startsWith("percent") ? "%" : ""} ${r.calcType.replace(/_/g, " ")}` },
            { key: "taxable", header: "Taxable", render: (r) => r.taxable ? "Yes" : "No" },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr ? <Button size="sm" variant="ghost" onClick={async () => { await patch(`/payroll/components/${r._id}`, { active: false }); refresh(); }}>Retire</Button> : null,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No components" description="Initialize HRMS defaults in HR Settings, or add components below." />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Add component" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Special allowance" /></Field>
            <Field label="Type">
              <Select value={form.part} onChange={(e) => setForm((f) => ({ ...f, part: e.target.value }))}>
                {["earning", "deduction", "employer_cost"].map((p) => <option key={p} value={p}>{cap(p)}</option>)}
              </Select>
            </Field>
            <Field label="Calculation">
              <Select value={form.calcType} onChange={(e) => setForm((f) => ({ ...f, calcType: e.target.value }))}>
                {["percent_of_gross", "percent_of_basic", "fixed", "balancing"].map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </Select>
            </Field>
            <Field label="Value"><Input type="number" value={form.calcValue} onChange={(e) => setForm((f) => ({ ...f, calcValue: e.target.value }))} placeholder="40" /></Field>
            <Button variant="primary" onClick={add}>Add</Button>
          </Row>
        </SectionCard>
      )}
    </Stack>
  );
}

// ── Reimbursements ──
function Reimb({ t }) {
  const { post } = useHrms();
  const { data, loading, refresh } = useFetch("/payroll/reimbursements");
  const inbox = useFetch("/approvals/inbox?type=reimbursement");

  const decide = async (id, action) => {
    await post(`/approvals/${id}/decide`, { action });
    refresh(); inbox.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <Hint>Approved claims are added to net pay in the next computed run and marked paid when the run locks.</Hint>

      {(inbox.data || []).length > 0 && (
        <SectionCard title="Waiting on you" padding={0} actions={<Badge tone="warning">{inbox.data.length}</Badge>}>
          <DataTable
            columns={[
              { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
              { key: "summary", header: "Claim", render: (r) => r.summary },
              {
                key: "act", header: "", align: "right",
                render: (r) => (
                  <Row gap={4} justify="flex-end">
                    <Button size="sm" variant="primary" onClick={() => decide(r._id, "approve")}>Approve</Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(r._id, "reject")}>Reject</Button>
                  </Row>
                ),
              },
            ]}
            rows={inbox.data}
          />
        </SectionCard>
      )}

      <SectionCard title="All claims" subtitle="Raise one from My HR ▸ Requests" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
            { key: "head", header: "Head" },
            { key: "amount", header: "Amount", align: "right", render: (r) => <Num t={t} bold>{fmtMoney(r.amount)}</Num> },
            { key: "billAttached", header: "Bill", render: (r) => r.billUrl
              ? <a href={r.billUrl} target="_blank" rel="noreferrer" style={{ fontSize: t.font.size.sm, color: t.c.primary }}>View bill</a>
              : <Badge tone={r.billAttached ? "positive" : "negative"}>{r.billAttached ? "Attached" : "Missing"}</Badge> },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No claims" description="Reimbursement claims appear here." />}
        />
      </SectionCard>
    </Stack>
  );
}

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, " ") : "—");
